import type { EditProps } from "react-admin";
import {
  TextInput,
  Edit,
  SimpleForm,
  SelectInput,
  BooleanInput,
  DateInput,
  TopToolbar,
  ShowButton,
  ListButton,
  usePermissions,
} from "react-admin";
import { EMPLOYEE_TYPE } from "@/constants";
import type { PermissionRecord } from "@/types/permissions";
import { hasPermission } from "@/utils/permission-utils";
import { StaffPayrollSetupIncompleteBanner } from "@/containers/staff/shared/StaffPayrollSetupIncompleteBanner";

import { StaffJmsLoginSection } from "./components/StaffJmsLoginSection";
import { StaffLeaveNotifyContactsSection } from "./components/StaffLeaveNotifyContactsSection";
import { StaffSalaryHistorySection } from "./components/StaffSalaryHistorySection";
import {
  EMPLOYEE_GENDER_CHOICES,
  transformStaffEmployeePayload,
  type StaffEmployeeFormValues,
} from "./staff-employee-form";

const StaffEditActions = () => (
  <TopToolbar>
    <ShowButton label="Staff dashboard" />
    <ListButton />
  </TopToolbar>
);

const StaffEditForm = () => {
  const { permissions } = usePermissions<PermissionRecord>();
  const canEditLogin = hasPermission(permissions, "employees.edit");

  return (
    <SimpleForm warnWhenUnsavedChanges sx={{ maxWidth: 560 }}>
      <StaffPayrollSetupIncompleteBanner dense />
      <TextInput source="name" isRequired fullWidth />
      <TextInput source="phone" isRequired fullWidth />
      <SelectInput
        label="Type"
        source="type"
        key={1}
        choices={Object.entries(EMPLOYEE_TYPE).map(([id, name]) => ({ id, name }))}
        sx={{ marginBottom: 0 }}
        alwaysOn
      />
      <DateInput source="joinedOn" label="Joined on" fullWidth />
      <DateInput source="leftOn" label="Left on" fullWidth />
      <SelectInput
        source="gender"
        label="Gender"
        choices={EMPLOYEE_GENDER_CHOICES}
        emptyText="—"
        resettable
        fullWidth
        helperText="Required for Maharashtra professional tax when payroll is enabled"
      />
      <BooleanInput
        label="Receive Booking Summaries via WhatsApp"
        source="receiveBookingSummary"
        fullWidth
        defaultValue={false}
      />
      <BooleanInput label="Active" source="isActive" fullWidth defaultValue={true} />
      {canEditLogin ? <StaffJmsLoginSection /> : null}
      <StaffSalaryHistorySection />
      <StaffLeaveNotifyContactsSection />
    </SimpleForm>
  );
};

const StaffEdit = (props: EditProps) => (
  <Edit
    {...props}
    mutationMode="optimistic"
    actions={<StaffEditActions />}
    transform={(data) => transformStaffEmployeePayload(data as StaffEmployeeFormValues)}
  >
    <StaffEditForm />
  </Edit>
);

export default StaffEdit;
